import { createAsyncThunk } from "@reduxjs/toolkit"

import i18n from "../../../i18n.ts"
import { showNotification } from "../../../utils/notification/show-notification"
import * as api from "../api/services.ts"
import {
  forgetAttachments,
  recallAttachments,
  rememberAttachments
} from "../helpers/pending-attachments.ts"
import type { Message, NotificationLevel } from "../types/messaging.types.ts"
import {
  messageArrived,
  messageChanged,
  messageDiscarded,
  messageFailed,
  messageQueued,
  messageRetrying,
  prependMessages,
  setConversations,
  setConversationsError,
  setConversationsLoading,
  setDraft,
  setMessages,
  setMessagesError,
  setMessagesLoading,
  setNotificationLevelFor,
  setUnreadCount,
  setUnreadTotal,
  upsertConversation
} from "./messaging.slice.ts"

interface Scope {
  portal?: boolean
}

export const fetchConversations = createAsyncThunk(
  "messaging/fetchConversations",
  async (
    params: {
      kind?: string
      project?: string
      unread?: string
      q?: string
    } & Scope,
    { dispatch }
  ) => {
    const { portal, ...query } = params
    dispatch(setConversationsLoading(true))
    const response = await api.getConversations(query, portal)
    if (!response.ok || !response.data) {
      dispatch(setConversationsError(true))
      return
    }
    dispatch(setConversations(response.data))
  }
)

export const fetchMessages = createAsyncThunk(
  "messaging/fetchMessages",
  async (params: { conversationId: string } & Scope, { dispatch }) => {
    dispatch(
      setMessagesLoading({ conversationId: params.conversationId, loading: true })
    )
    const response = await api.getMessages(
      params.conversationId,
      {},
      params.portal
    )
    if (!response.ok || !response.data) {
      dispatch(
        setMessagesError({ conversationId: params.conversationId, error: true })
      )
      return
    }
    dispatch(
      setMessages({
        conversationId: params.conversationId,
        messages: response.data.results,
        hasMore: response.data.has_more
      })
    )
  }
)

export const fetchOlderMessages = createAsyncThunk(
  "messaging/fetchOlderMessages",
  async (
    params: { conversationId: string; before: string } & Scope,
    { dispatch }
  ) => {
    const response = await api.getMessages(
      params.conversationId,
      { before: params.before },
      params.portal
    )
    if (!response.ok || !response.data) return
    dispatch(
      prependMessages({
        conversationId: params.conversationId,
        messages: response.data.results,
        hasMore: response.data.has_more
      })
    )
  }
)

/**
 * Everything the socket could not deliver while it was down.
 * `after` is the last message this client actually holds, so the catch-up is
 * exactly the gap — never a whole page to reconcile.
 */
export const catchUpMessages = createAsyncThunk(
  "messaging/catchUp",
  async (
    params: { conversationId: string; after: string } & Scope,
    { dispatch }
  ) => {
    const response = await api.getMessages(
      params.conversationId,
      { after: params.after },
      params.portal
    )
    if (!response.ok || !response.data) return
    response.data.results.forEach((message) => dispatch(messageArrived(message)))
  }
)

export const sendMessage = createAsyncThunk(
  "messaging/send",
  async (
    params: {
      conversationId: string
      body: string
      replyTo?: Message["reply_to"]
      attachments?: File[]
      clientToken: string
      onProgress?: (percent: number) => void
    } & Scope,
    { dispatch }
  ) => {
    dispatch(
      messageQueued({
        client_token: params.clientToken,
        conversation: params.conversationId,
        body: params.body,
        reply_to: params.replyTo ?? null,
        attachment_names: (params.attachments ?? []).map((file) => file.name),
        created: new Date().toISOString(),
        status: "sending"
      })
    )

    // Held outside the store so a retry can resend the bytes; the pending row
    // only carries filenames.
    rememberAttachments(params.clientToken, params.attachments ?? [])

    const response = await api.sendMessage(
      params.conversationId,
      {
        body: params.body,
        reply_to: params.replyTo?.id,
        client_token: params.clientToken,
        attachments: params.attachments
      },
      params.portal,
      params.onProgress
    )

    if (!response.ok || !response.data) {
      // The files stay remembered: the retry needs them.
      dispatch(messageFailed(params.clientToken))
      return
    }
    forgetAttachments(params.clientToken)
    // `messageArrived` drops the pending row keyed by this same token, so the
    // optimistic bubble is replaced rather than joined.
    dispatch(messageArrived(response.data))
  }
)

export const retrySend = createAsyncThunk(
  "messaging/retrySend",
  async (
    params: {
      clientToken: string
      conversationId: string
      body: string
      hadAttachments?: boolean
      onProgress?: (percent: number) => void
    } & Scope,
    { dispatch }
  ) => {
    // A message that went out with attachments must not come back as text.
    // After a reload the `File` objects are gone, and there is no way to
    // resend them — say so rather than posting a stripped message under the
    // same token, which would look like it succeeded.
    const files = recallAttachments(params.clientToken)
    if (params.hadAttachments && !files?.length) {
      dispatch(messageFailed(params.clientToken))
      showNotification.error({
        message: i18n.t(
          "messages.errors.attachmentsLost",
          "The files are no longer available to resend. Please attach them again."
        ) as string
      })
      return
    }

    dispatch(messageRetrying(params.clientToken))
    // The same token goes back, so a send that actually landed before the
    // timeout returns the original message instead of writing a second one.
    const response = await api.sendMessage(
      params.conversationId,
      {
        body: params.body,
        client_token: params.clientToken,
        attachments: files
      },
      params.portal,
      params.onProgress
    )
    if (!response.ok || !response.data) {
      dispatch(messageFailed(params.clientToken))
      return
    }
    forgetAttachments(params.clientToken)
    dispatch(messageArrived(response.data))
  }
)

export const discardFailedMessage = createAsyncThunk(
  "messaging/discardFailed",
  async (clientToken: string, { dispatch }) => {
    forgetAttachments(clientToken)
    dispatch(messageDiscarded(clientToken))
  }
)

export const editMessage = createAsyncThunk(
  "messaging/edit",
  async (params: { messageId: string; body: string } & Scope, { dispatch }) => {
    const response = await api.editMessage(
      params.messageId,
      params.body,
      params.portal
    )
    if (!response.ok || !response.data) {
      showNotification.error({
        message: i18n.t("messages.errors.editFailed") as string
      })
      return
    }
    dispatch(messageChanged(response.data))
  }
)

export const deleteMessage = createAsyncThunk(
  "messaging/delete",
  async (params: { messageId: string } & Scope, { dispatch }) => {
    const response = await api.deleteMessage(params.messageId, params.portal)
    if (!response.ok || !response.data) {
      showNotification.error({
        message: i18n.t("messages.errors.deleteFailed") as string
      })
      return
    }
    dispatch(messageChanged(response.data))
  }
)

export const markRead = createAsyncThunk(
  "messaging/markRead",
  async (
    params: { conversationId: string; upTo: string; upToMessage?: string } & Scope,
    { dispatch }
  ) => {
    const response = await api.markConversationRead(
      params.conversationId,
      params.upTo,
      params.portal,
      params.upToMessage
    )
    if (!response.ok || !response.data) return
    dispatch(
      setUnreadCount({
        conversationId: params.conversationId,
        count: response.data.unread_count
      })
    )
  }
)

export const updateNotificationLevel = createAsyncThunk(
  "messaging/updateNotificationLevel",
  async (
    params: {
      conversationId: string
      level: NotificationLevel
    } & Scope,
    { dispatch }
  ) => {
    const response = await api.setNotificationLevel(
      params.conversationId,
      params.level,
      params.portal
    )
    if (!response.ok) return
    dispatch(
      setNotificationLevelFor({
        conversationId: params.conversationId,
        level: params.level
      })
    )
  }
)

export const persistDraft = createAsyncThunk(
  "messaging/persistDraft",
  async (
    params: { conversationId: string; draft: string } & Scope,
    { dispatch }
  ) => {
    dispatch(
      setDraft({ conversationId: params.conversationId, draft: params.draft })
    )
    await api.saveDraft(params.conversationId, params.draft, params.portal)
  }
)

export const fetchUnreadTotal = createAsyncThunk(
  "messaging/fetchUnreadTotal",
  async (params: Scope | undefined, { dispatch }) => {
    const response = await api.getUnreadTotal(params?.portal)
    if (!response.ok || !response.data) return
    dispatch(setUnreadTotal(response.data.unread_total))
  }
)

export const openDirectConversation = createAsyncThunk(
  "messaging/openDirect",
  async (params: { userId: string; recordId: string }, { dispatch }) => {
    const response = await api.openDirectConversation(
      params.userId,
      params.recordId
    )
    if (!response.ok || !response.data) {
      showNotification.error({
        message: i18n.t("messages.errors.directFailed") as string
      })
      return null
    }
    dispatch(upsertConversation(response.data))
    return response.data
  }
)
